/**
 * Back-button integration for `<theme-drawer>`.
 *
 * Makes the hardware/browser Back button close an open drawer instead of
 * navigating away from the page — the behaviour users expect from a
 * fullscreen overlay on mobile.
 *
 * How it works: each drawer opened by user action pushes a marker history
 * entry. Back pops the marker, which fires `popstate`, which closes the
 * topmost drawer. Closing via the UI (close button, Escape, backdrop click)
 * calls `history.back()` to consume the marker so it doesn't linger and
 * swallow a later Back press.
 *
 * The URL is deliberately left unchanged: `pushState(state, '', location.href)`
 * pushes an entry with no visible URL change, so the drawer stays invisible to
 * bookmarking and sharing, and cannot collide with the PDP's Section Rendering
 * API toggles which manipulate the URL themselves.
 *
 * Only applies in modal mode (< 990px). On wide viewports the drawer is a
 * sidebar that squeezes page content rather than an overlay, and it survives
 * navigation via sessionStorage — hijacking Back there would be surprising.
 */

/** Viewport width below which drawers open as a modal overlay. Mirrors MODAL_BREAKPOINT in theme-drawer.js. */
const MODAL_BREAKPOINT = 990;

/** Marker written into history.state to identify entries this module pushed. */
const MARKER = 'theme-drawer-history';

const modalQuery = window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`);

/**
 * Drawers we have pushed a history entry for, oldest first. Mirrors the
 * stack of marker entries sitting on the history stack.
 * @type {HTMLElement[]}
 */
let tracked = [];

/**
 * True while we are closing a drawer in response to `popstate`. The resulting
 * `theme-drawer:close` event must not call history.back() — the entry it would
 * pop has already been consumed by the user's Back press.
 */
let closingFromPopstate = false;

/**
 * True while we are calling history.back() ourselves to consume a marker after
 * a UI-initiated close. The resulting `popstate` must not close another drawer.
 */
let unwindingAfterClose = false;

document.addEventListener('theme-drawer:open', (event) => {
  const drawer = event.target;
  if (!(drawer instanceof HTMLElement)) return;

  // Only overlay-mode drawers get Back-to-close.
  //
  // This also excludes drawers restored from sessionStorage on page load: the
  // restore script in theme-drawer.liquid runs only at >= 990px, so a restored
  // drawer never reaches this line and never gets a marker it didn't push.
  if (!modalQuery.matches) return;

  // theme-drawer:open re-fires when an already-open drawer is brought to the
  // front of the stack. That must not push a second entry for the same drawer.
  if (tracked.includes(drawer)) return;

  tracked.push(drawer);
  history.pushState({ [MARKER]: tracked.length }, '', location.href);
});

document.addEventListener('theme-drawer:close', (event) => {
  const drawer = event.target;
  if (!(drawer instanceof HTMLElement)) return;

  const index = tracked.indexOf(drawer);
  if (index === -1) return;

  tracked.splice(index, 1);

  // Closed by popstate — the marker is already gone from the history stack.
  if (closingFromPopstate) return;

  // Closed via the UI. Consume the marker so a later Back press navigates the
  // page rather than popping a stale drawer entry.
  //
  // Only safe when this drawer owns the newest marker. If a drawer lower in the
  // stack is closed out of order, its marker is buried under another drawer's
  // and cannot be removed without disturbing that one; we leave it in place and
  // let the popstate handler discard it harmlessly.
  if (index !== tracked.length) return;

  unwindingAfterClose = true;
  history.back();
});

window.addEventListener('popstate', () => {
  if (unwindingAfterClose) {
    unwindingAfterClose = false;
    return;
  }

  const drawer = tracked.pop();
  if (!drawer) return;

  // Stale entry: the drawer was removed from the DOM (e.g. replaced by a
  // Section Rendering API morph) or closed by some other path.
  if (!drawer.isConnected || !drawer.hasAttribute('open')) return;

  closingFromPopstate = true;
  try {
    /** @type {{ close?: () => void }} */ (drawer).close?.();
  } finally {
    closingFromPopstate = false;
  }
});
